import React, { useCallback, useEffect, useState } from 'react';
import { TerminalSquare, Loader2 } from 'lucide-react';
import { getRestApiBearerToken } from '@stepflow/ui-shared/services/apiToken';
import { humanizeAgentName } from '@stepflow/ui-shared/utils/agentDisplayName.js';
import BuilderStreamTerminal from './BuilderStreamTerminal.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// TRANSCRIPT CACHE (per-session, module-scoped): a builder's S3 transcript is IMMUTABLE once the
// run is done, so re-fetching it every time the user re-clicks that builder row is pure waste (a
// presign round-trip + an S3 GET). Cache the parsed events by (networkId, transcript key) so a
// remount — the reader keys BuilderLogCard per builder, so switching back remounts it — paints
// instantly from memory. Bounded to keep it from growing unboundedly across a long room session.
const _transcriptCache = new Map();
const _CACHE_MAX = 40;
function _cacheKey(networkId, eventsKey, logKey) {
  return `${networkId || ''}::${eventsKey || logKey || ''}`;
}
function _cacheGet(k) {
  return k ? _transcriptCache.get(k) || null : null;
}
function _cacheSet(k, events) {
  if (!k || !Array.isArray(events)) return;
  if (_transcriptCache.size >= _CACHE_MAX) {
    // Evict oldest (Map preserves insertion order) — simple LRU-ish bound.
    const oldest = _transcriptCache.keys().next().value;
    if (oldest !== undefined) _transcriptCache.delete(oldest);
  }
  _transcriptCache.set(k, events);
}

async function fetchBuilderFile(networkId, key) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const q = new URLSearchParams({ key });
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/files/download?${q}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.downloadUrl) {
    const lr = await fetch(data.downloadUrl);
    if (!lr.ok) throw new Error('Failed to fetch');
    return lr;
  }
  throw new Error('No download URL');
}

export default function BuilderLogCard({ completion, networkId, runId, agentName, autoLoad = false, hideHeader = false, fill = false }) {
  const exitCode = completion?.exitCode;
  const logKey = completion?.logKey || '';
  const eventsKey = completion?.eventsKey || '';
  const succeeded = exitCode === 0;

  // Seed from the transcript cache so a re-clicked (remounted) builder paints instantly with no
  // fetch. The reader keys this card per builder, so ``events`` never carries a sibling's log.
  const [events, setEvents] = useState(() => _cacheGet(_cacheKey(networkId, eventsKey, logKey)));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadLog = useCallback(async () => {
    if (!networkId || (!eventsKey && !logKey)) {
      setError('Session data unavailable');
      return;
    }
    // Cache hit → skip the network entirely (immutable transcript for a done run).
    const ck = _cacheKey(networkId, eventsKey, logKey);
    const cached = _cacheGet(ck);
    if (cached) {
      setEvents(cached);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Try structured events first (full Claude Code terminal experience)
      if (eventsKey) {
        const resp = await fetchBuilderFile(networkId, eventsKey);
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) {
          _cacheSet(ck, data);
          setEvents(data);
          return;
        }
      }
      // Fallback: raw log as plain text events
      if (logKey) {
        const resp = await fetchBuilderFile(networkId, logKey);
        const text = await resp.text();
        const lines = text.split('\n').filter(Boolean).map((line) => ({ cc: 'text', text: line }));
        _cacheSet(ck, lines);
        setEvents(lines);
      }
    } catch (e) {
      setError(e.message || 'Failed to load log');
    } finally {
      setLoading(false);
    }
  }, [networkId, logKey, eventsKey]);

  // AUTO-LOAD (shared reader): the user already clicked this builder's tree row to see it — the
  // header + "Load session" gate is redundant there (it's the classic INLINE card's affordance so
  // we don't mass-fetch every historic run). Fire the fetch once on mount when the keys are ready
  // so the transcript fills the reader directly. No-op if already loading/loaded.
  useEffect(() => {
    if (autoLoad && !events && !loading && (eventsKey || logKey)) loadLog();
  }, [autoLoad, events, loading, eventsKey, logKey, loadLog]);


  // If events are loaded, render the full Claude Code terminal
  if (events) {
    return (
      <BuilderStreamTerminal
        lines={events}
        status={succeeded ? 'completed' : 'failed'}
        networkId={networkId}
        runId={runId}
        agentName={agentName}
        hideHeader={hideHeader}
        fill={fill}
      />
    );
  }

  // AUTO-LOAD in progress (shared reader) — show a terminal-styled spinner, not the "Load session"
  // header the user already implicitly chose by clicking the row. On error, fall through to the
  // header below so they can retry.
  if (autoLoad && loading && !error) {
    return (
      <div className={`flex items-center gap-2 bg-[#0d1117] px-3 py-2 text-[11px] text-slate-400 ${
        fill ? 'h-full' : 'rounded-lg border border-slate-800'}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" aria-hidden />
        <span>Loading builder session…</span>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-[#0d1117] shadow-md">
      <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-900/95 px-3 py-1.5 cursor-pointer select-none" onDoubleClick={loadLog}>
        <TerminalSquare className="h-3.5 w-3.5 text-slate-400" aria-hidden />
        {agentName ? (
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-300" title={agentName}>
            {humanizeAgentName(agentName)}
          </span>
        ) : null}
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
          succeeded
            ? 'bg-emerald-950/90 text-emerald-300'
            : 'bg-red-950/90 text-red-300'
        }`}>
          {succeeded ? 'done' : 'failed'}
        </span>
        {exitCode != null && (
          <span className="text-[10px] text-slate-400">exit {exitCode}</span>
        )}
        <button
          type="button"
          onClick={loadLog}
          disabled={loading}
          className="ml-auto flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-blue-400 hover:bg-slate-800 hover:text-blue-300 disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <TerminalSquare className="h-3 w-3" />
          )}
          Load session
        </button>
      </div>
      {error && (
        <div className="px-3 py-1.5 text-[11px] text-red-400">{error}</div>
      )}
      {completion?.error && !events && (
        <div className="px-3 py-1.5 text-[11px] text-slate-400 border-t border-slate-800">
          {completion.error}
        </div>
      )}
    </div>
  );
}
